import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { FaPlay, FaArrowLeft, FaArrowRight, FaCheck, FaTimes, FaVolumeUp, FaEye, FaEyeSlash, FaQuoteLeft } from 'react-icons/fa';
import { isAudioSupported, playSmartAudio } from '../utils/audioUtils';
import { getLessonById } from '../data/hafezPoems';
import type { HafezLesson as HafezLessonType, HafezPoem, HafezQuizQuestion } from '../data/allLessons';

const HafezLesson: React.FC = () => {
  const { lessonId } = useParams<{ lessonId: string }>();
  const navigate = useNavigate();
  const [lesson, setLesson] = useState<HafezLessonType | null>(null);
  const [currentPoemIndex, setCurrentPoemIndex] = useState(0);
  const [currentLineIndex, setCurrentLineIndex] = useState(0);
  const [showWordAnalysis, setShowWordAnalysis] = useState(false);
  const [showQuiz, setShowQuiz] = useState(false);
  const [showQuizResults, setShowQuizResults] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState<{ [key: number]: string }>({});
  const [quizResults, setQuizResults] = useState<{ [key: number]: boolean }>({});
  const [completedPoems, setCompletedPoems] = useState<number[]>([]);

  useEffect(() => {
    if (lessonId) {
      let foundLesson = undefined;
      if (!isNaN(Number(lessonId))) {
        foundLesson = getLessonById(Number(lessonId));
      }
      if (!foundLesson) {
        foundLesson = getLessonById(lessonId);
      }
      if (foundLesson) {
        setLesson(foundLesson);
      } else {
        navigate('/hafez');
      }
    }
  }, [lessonId, navigate]);

  useEffect(() => {
    const savedProgress = localStorage.getItem(`hafez-lesson-${lessonId}`);
    if (savedProgress) {
      const progress = JSON.parse(savedProgress);
      setCompletedPoems(progress.completedPoems || []);
    }
  }, [lessonId]);

  const saveProgress = () => {
    const progress = {
      completedPoems,
      completedAt: new Date().toISOString()
    };
    localStorage.setItem(`hafez-lesson-${lessonId}`, JSON.stringify(progress));
    
    const overallProgress = localStorage.getItem('hafez-progress');
    const currentProgress = overallProgress ? JSON.parse(overallProgress) : { completedLessons: [], totalProgress: 0 };
    
    if (!currentProgress.completedLessons.includes(parseInt(lessonId!))) {
      currentProgress.completedLessons.push(parseInt(lessonId!));
      // Fix: Use actual number of lessons instead of hardcoded 2
      const totalLessons = 2; // This should be dynamically calculated based on actual lessons
      currentProgress.totalProgress = Math.min((currentProgress.completedLessons.length / totalLessons) * 100, 100);
      localStorage.setItem('hafez-progress', JSON.stringify(currentProgress));
    }
  };

  const handlePoemComplete = (poemId: number) => {
    if (!completedPoems.includes(poemId)) {
      setCompletedPoems(prev => [...prev, poemId]);
      saveProgress();
    }
  };

  const handleNextLine = () => {
    if (!lesson) return;
    const currentPoem = lesson.poems[currentPoemIndex];
    
    if (currentLineIndex < currentPoem.lines.length - 1) {
      setCurrentLineIndex(prev => prev + 1);
      setShowWordAnalysis(false);
    } else if (currentPoemIndex < lesson.poems.length - 1) {
      setCurrentPoemIndex(prev => prev + 1);
      setCurrentLineIndex(0);
      setShowWordAnalysis(false);
    } else {
      setShowQuiz(true);
    }
  };

  const handlePreviousLine = () => {
    if (currentLineIndex > 0) {
      setCurrentLineIndex(prev => prev - 1);
      setShowWordAnalysis(false);
    } else if (currentPoemIndex > 0) {
      setCurrentPoemIndex(prev => prev - 1);
      const previousPoem = lesson!.poems[currentPoemIndex - 1];
      setCurrentLineIndex(previousPoem.lines.length - 1);
      setShowWordAnalysis(false);
    }
  };

  const handleQuizAnswer = (questionId: number, answer: string) => {
    setQuizAnswers(prev => ({ ...prev, [questionId]: answer }));
  };

  const handleQuizSubmit = () => {
    if (!lesson) return;

    const results: { [key: number]: boolean } = {};
    lesson.quiz.forEach(quizQuestion => {
      const userAnswer = quizAnswers[quizQuestion.id];
      results[quizQuestion.id] = userAnswer === quizQuestion.correctAnswer;
    });

    setQuizResults(results);
    setShowQuizResults(true);
    saveProgress();
  };

  const getQuizScore = () => {
    if (!lesson) return 0;
    const correctAnswers = Object.values(quizResults).filter(result => result).length;
    return Math.round((correctAnswers / lesson.quiz.length) * 100);
  };

  if (!lesson) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  const currentPoem = lesson.poems[currentPoemIndex];
  const currentLine = currentPoem.lines[currentLineIndex];
  const progress = (completedPoems.length / lesson.poems.length) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-gray-900 dark:to-gray-800">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <Link
            to="/hafez"
            className="flex items-center text-gray-600 dark:text-gray-400 hover:text-purple-500 dark:hover:text-purple-400 transition-colors"
          >
            <FaArrowLeft className="mr-2" />
            Back to Lessons
          </Link>
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              {lesson.title}
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              {lesson.persianTitle}
            </p>
          </div>
          <div className="w-24"></div>
        </div>

        {/* Progress Bar */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg mb-8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">
              Progress
            </h3>
            <span className="text-lg font-bold text-purple-500">
              {Math.round(progress)}%
            </span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
            <div 
              className="bg-gradient-to-r from-purple-500 to-indigo-500 h-3 rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
          <div className="flex justify-between mt-2 text-sm text-gray-600 dark:text-gray-400">
            <span>Poem {currentPoemIndex + 1} of {lesson.poems.length}, Line {currentLineIndex + 1} of {currentPoem.lines.length}</span>
            <span>{completedPoems.length} completed</span>
          </div>
        </div>

        {!showQuiz ? (
          /* Poetry Section */
          <div className="max-w-6xl mx-auto">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden">
              {/* Poem Header */}
              <div className="p-8 border-b border-gray-200 dark:border-gray-700">
                <div className="text-center mb-6">
                  <div className="flex items-center justify-center mb-4">
                    <FaQuoteLeft className="text-3xl text-purple-500 mr-3" />
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                      {currentPoem.title}
                    </h2>
                  </div>
                  <p className="text-gray-600 dark:text-gray-400">
                    {currentPoem.persianTitle}
                  </p>
                </div>

                {/* Poem Info */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4 text-center">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-1">Theme</h4>
                    <p className="text-purple-600 dark:text-purple-400">{currentPoem.theme}</p>
                  </div>
                  <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-lg p-4 text-center">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-1">Difficulty</h4>
                    <p className="text-indigo-600 dark:text-indigo-400 capitalize">{currentPoem.difficulty}</p>
                  </div>
                  <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 text-center">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-1">Lines</h4>
                    <p className="text-green-600 dark:text-green-400">{currentPoem.lines.length}</p>
                  </div>
                </div>

                {/* Cultural Notes */}
                <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Cultural Context</h4>
                  <p className="text-gray-700 dark:text-gray-300 text-sm">
                    {currentPoem.culturalNotes}
                  </p>
                </div>
              </div>

              {/* Current Line */}
              <div className="p-8">
                <div className="text-center mb-8">
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                    Line {currentLineIndex + 1}
                  </h3>
                  
                  {/* Persian Text */}
                  <div className="bg-gradient-to-r from-purple-100 to-indigo-100 dark:from-purple-900/20 dark:to-indigo-900/20 rounded-xl p-6 mb-6">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-lg font-bold text-gray-900 dark:text-white">
                        Persian Text
                      </h4>
                      {isAudioSupported() && (
                        <button
                          onClick={() => playSmartAudio(undefined, currentLine.persian).catch(console.error)}
                          className="p-3 bg-purple-500 text-white rounded-full hover:bg-purple-600 transition-colors"
                        >
                          <FaVolumeUp />
                        </button>
                      )}
                    </div>
                    <p className="text-3xl font-bold text-gray-900 dark:text-white mb-4 leading-relaxed">
                      {currentLine.persian}
                    </p>
                    <p className="text-lg text-gray-600 dark:text-gray-400">
                      {currentLine.transliteration}
                    </p>
                  </div>

                  {/* English Translation */}
                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-6 mb-6">
                    <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
                      English Translation
                    </h4>
                    <p className="text-xl text-gray-900 dark:text-white leading-relaxed">
                      {currentLine.english}
                    </p>
                  </div>

                  {/* Word Analysis */}
                  <div className="mb-6">
                    <button
                      onClick={() => setShowWordAnalysis(!showWordAnalysis)}
                      className="flex items-center mx-auto px-6 py-3 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                    >
                      {showWordAnalysis ? <FaEyeSlash className="mr-2" /> : <FaEye className="mr-2" />}
                      {showWordAnalysis ? 'Hide' : 'Show'} Word Analysis
                    </button>
                  </div>

                  {showWordAnalysis && (
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-6 mb-6">
                      <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
                        Word-by-Word Translation
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {currentLine.wordByWord.map((word, index) => (
                          <div key={index} className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                            <div className="text-center mb-2">
                              <p className="text-lg font-bold text-gray-900 dark:text-white">
                                {word.persian}
                              </p>
                              <p className="text-sm text-gray-600 dark:text-gray-400">
                                {word.transliteration}
                              </p>
                            </div>
                            <div className="text-center">
                              <p className="text-gray-900 dark:text-white font-medium">
                                {word.english}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400 italic">
                                {word.partOfSpeech}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Navigation */}
                  <div className="flex justify-between items-center">
                    <button
                      onClick={handlePreviousLine}
                      disabled={currentPoemIndex === 0 && currentLineIndex === 0}
                      className={`flex items-center px-6 py-3 rounded-lg font-semibold transition-colors ${
                        currentPoemIndex === 0 && currentLineIndex === 0
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'bg-gray-500 text-white hover:bg-gray-600'
                      }`}
                    >
                      <FaArrowLeft className="mr-2" />
                      Previous
                    </button>

                    <div className="flex space-x-2 rtl:space-x-reverse">
                      {lesson.poems.map((poem, poemIndex) => 
                        poem.lines.map((_, lineIndex) => (
                          <div
                            key={`${poemIndex}-${lineIndex}`}
                            className={`w-2 h-2 rounded-full ${
                              poemIndex === currentPoemIndex && lineIndex === currentLineIndex
                                ? 'bg-purple-500'
                                : completedPoems.includes(poem.id)
                                ? 'bg-green-500'
                                : 'bg-gray-300'
                            }`}
                          />
                        ))
                      )}
                    </div>

                    <button
                      onClick={handleNextLine}
                      className="flex items-center px-6 py-3 bg-gradient-to-r from-purple-500 to-indigo-500 text-white rounded-lg hover:from-purple-600 hover:to-indigo-600 transition-all duration-300 font-semibold"
                    >
                      {currentPoemIndex === lesson.poems.length - 1 && currentLineIndex === currentPoem.lines.length - 1 ? (
                        <>
                          Take Quiz
                          <FaArrowRight className="ml-2" />
                        </>
                      ) : (
                        <>
                          Next
                          <FaArrowRight className="ml-2" />
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* Quiz Section */
          <div className="max-w-4xl mx-auto">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden">
              <div className="p-8">
                <div className="text-center mb-8">
                  <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                    Lesson Quiz
                  </h2>
                  <p className="text-gray-600 dark:text-gray-400">
                    Test your understanding of Hafez's poetry
                  </p>
                </div>

                {!showQuizResults ? (
                  <div className="space-y-8">
                    {lesson.quiz.map((quizQuestion, index) => (
                      <div key={quizQuestion.id} className="bg-gray-50 dark:bg-gray-700 rounded-xl p-6">
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
                          Question {index + 1}: {quizQuestion.question}
                        </h3>

                        {quizQuestion.type === 'multiple-choice' && quizQuestion.options && (
                          <div className="space-y-3">
                            {quizQuestion.options.map((option, optionIndex) => (
                              <button
                                key={optionIndex}
                                onClick={() => handleQuizAnswer(quizQuestion.id, option)}
                                className={`w-full p-4 text-left rounded-lg border-2 transition-colors ${
                                  quizAnswers[quizQuestion.id] === option
                                    ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                                    : 'border-gray-200 dark:border-gray-600 hover:border-purple-300'
                                }`}
                              >
                                <span className="font-medium text-gray-900 dark:text-white">
                                  {String.fromCharCode(65 + optionIndex)}. {option}
                                </span>
                              </button>
                            ))}
                          </div>
                        )}

                        {quizQuestion.type === 'translation' && (
                          <div className="space-y-3">
                            <input
                              type="text"
                              placeholder="Type your answer..."
                              value={quizAnswers[quizQuestion.id] || ''}
                              onChange={(e) => handleQuizAnswer(quizQuestion.id, e.target.value)}
                              className="w-full p-4 border-2 border-gray-200 dark:border-gray-600 rounded-lg focus:border-purple-500 focus:outline-none bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                            />
                          </div>
                        )}
                      </div>
                    ))}

                    <div className="text-center">
                      <button
                        onClick={handleQuizSubmit}
                        className="px-8 py-4 bg-gradient-to-r from-purple-500 to-indigo-500 text-white font-semibold rounded-lg hover:from-purple-600 hover:to-indigo-600 transition-all duration-300"
                      >
                        Submit Quiz
                      </button>
                    </div>
                  </div>
                ) : (
                  /* Quiz Results */
                  <div className="text-center">
                    <div className="mb-8">
                      <div className="text-6xl mb-4">🎉</div>
                      <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                        Quiz Complete!
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400 mb-4">
                        You scored {getQuizScore()}% on this lesson
                      </p>
                      <div className="text-4xl font-bold text-purple-500">
                        {getQuizScore()}%
                      </div>
                    </div>

                    <div className="space-y-4 mb-8">
                      {lesson.quiz.map((quizQuestion, index) => (
                        <div key={quizQuestion.id} className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-semibold text-gray-900 dark:text-white">
                              Question {index + 1}
                            </span>
                            {quizResults[quizQuestion.id] ? (
                              <FaCheck className="text-green-500 text-xl" />
                            ) : (
                              <FaTimes className="text-red-500 text-xl" />
                            )}
                          </div>
                          <p className="text-gray-600 dark:text-gray-400 text-sm mb-2">
                            {quizQuestion.question}
                          </p>
                          <p className="text-sm">
                            <span className="text-gray-500 dark:text-gray-400">Your answer:</span>{' '}
                            <span className="text-gray-900 dark:text-white">
                              {quizAnswers[quizQuestion.id] || 'No answer'}
                            </span>
                          </p>
                          <p className="text-sm">
                            <span className="text-gray-500 dark:text-gray-400">Correct answer:</span>{' '}
                            <span className="text-green-600 dark:text-green-400 font-semibold">
                              {quizQuestion.correctAnswer}
                            </span>
                          </p>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                            {quizQuestion.explanation}
                          </p>
                        </div>
                      ))}
                    </div>

                    <div className="space-y-4">
                      <Link
                        to="/hafez"
                        className="inline-block px-8 py-3 bg-gradient-to-r from-purple-500 to-indigo-500 text-white font-semibold rounded-lg hover:from-purple-600 hover:to-indigo-600 transition-all duration-300"
                      >
                        Back to Lessons
                      </Link>
                      <br />
                      <Link
                        to={`/hafez/flashcards/${lesson.id}`}
                        className="inline-block px-8 py-3 border-2 border-purple-500 text-purple-500 dark:text-purple-400 font-semibold rounded-lg hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-all duration-300"
                      >
                        Practice with Flashcards
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HafezLesson; 